import { useEffect, useRef } from 'react';

import {
  claimTrackingSlot,
  isTrackingClaimed,
} from 'utils/impressionTrackingStorage';

type UseImpressionTrackingParams = {
  trackingKey: string;
  isActive: boolean;
  onImpression: () => void;
  threshold?: number;
};

/**
 * IntersectionObserver による露出計測。
 * - isActive が false のときは observe しない（非アクティブスライドの誤発火防止）
 * - タブ存続中は 1 回に抑制（sessionStorage + in-memory）
 */
function useImpressionTracking<T extends Element = HTMLElement>({
  trackingKey,
  isActive,
  onImpression,
  threshold = 0.5,
}: UseImpressionTrackingParams) {
  const elementRef = useRef<T | null>(null);
  const onImpressionRef = useRef(onImpression);

  useEffect(() => {
    onImpressionRef.current = onImpression;
  }, [onImpression]);

  useEffect(() => {
    const element = elementRef.current;
    if (
      !element ||
      !isActive ||
      typeof IntersectionObserver === 'undefined' ||
      isTrackingClaimed(trackingKey)
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        if (!claimTrackingSlot(trackingKey)) return;

        onImpressionRef.current();
        observer.unobserve(entry.target);
      },
      { threshold },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [isActive, threshold, trackingKey]);

  return elementRef;
}

export default useImpressionTracking;
